import { useEffect, useState } from "react";
import { Plus, Download, Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "@/lib/api";

const CATEGORY_LABELS = {
  stationery: "Stationery",
  scrapbook_junk_journal: "Scrapbook & Junk Journal",
  coloring_pages: "Coloring Pages",
};

const STATUS_STYLES = {
  published: "bg-[#A8BFCE] text-ink",
  draft: "bg-ink/[0.07] text-ink/70",
  archived: "bg-ink/[0.04] text-ink/40",
};

const FILTERS = [{ id: "all", label: "All" }, ...Object.entries(CATEGORY_LABELS).map(([id, label]) => ({ id, label }))];

export const AdminDashboard = ({ onAdd, onEdit }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/freebies")
      .then(({ data }) => setItems(data.items))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const setStatus = async (item, status) => {
    setBusyId(item.id);
    try {
      await api.post(`/admin/freebies/${item.id}/status`, { status });
      toast.success(`${item.title} → ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async (item) => {
    setBusyId(item.id);
    try {
      await api.delete(`/admin/freebies/${item.id}`);
      toast.success(`${item.title} deleted`);
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const visible = filter === "all" ? items : items.filter((i) => i.category === filter);
  const totalDownloads = items.reduce((s, i) => s + (i.download_count || 0), 0);
  const totalHearts = items.reduce((s, i) => s + (i.heart_count || 0), 0);
  const published = items.filter((i) => i.status === "published").length;

  const actionBtn =
    "flex min-h-[44px] items-center justify-center rounded-full border border-ink/15 px-4 text-[12px] font-semibold uppercase tracking-[0.1em] text-ink/75 transition-colors hover:border-ink/40 hover:text-ink disabled:opacity-50";

  return (
    <div data-testid="admin-dashboard">
      <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="flex justify-end">
          <button
            type="button"
            data-testid="add-freebie-button"
            onClick={onAdd}
            className="flex min-h-[48px] items-center gap-2 rounded-full bg-[#6E8CA3] px-5 text-[12px] font-semibold uppercase tracking-[0.14em] text-ivory transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
          >
            <Plus className="h-4 w-4" /> Add New Freebie
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2" data-testid="admin-stats">
          <span className="flex items-center gap-1.5 rounded-full bg-white/70 px-4 py-2 text-xs font-medium text-ink/70">
            <Download className="h-3.5 w-3.5" /> {totalDownloads.toLocaleString()} downloads
          </span>
          <span className="flex items-center gap-1.5 rounded-full bg-white/70 px-4 py-2 text-xs font-medium text-ink/70">
            <Heart className="h-3.5 w-3.5" /> {totalHearts.toLocaleString()} hearts
          </span>
          <span className="rounded-full bg-white/70 px-4 py-2 text-xs font-medium text-ink/70">
            {published} published · {items.length} total
          </span>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              data-testid={`admin-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`min-h-[40px] rounded-full px-4 text-[12px] font-semibold transition-colors ${
                filter === f.id ? "bg-[#A8BFCE] text-ink" : "bg-white/70 text-ink/60 hover:text-ink"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20" data-testid="admin-loading">
            <Loader2 className="h-6 w-6 animate-spin text-ink/40" />
          </div>
        ) : visible.length === 0 ? (
          <p className="py-20 text-center text-sm text-ink/50" data-testid="admin-empty">
            No freebies here yet — tap “Add New Freebie” to upload your first one.
          </p>
        ) : (
          <ul className="mt-6 space-y-3" data-testid="admin-freebie-list">
            {visible.map((item) => (
              <li
                key={item.id}
                data-testid={`admin-item-${item.id}`}
                className="rounded-3xl bg-white/70 p-4 sm:p-5"
              >
                <div className="flex flex-wrap items-center gap-4">
                  <img
                    src={fileUrl(item.preview_url)}
                    alt=""
                    className="h-14 w-24 rounded-xl object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-serif text-lg font-medium text-ink">{item.title}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink/55">
                      <span>{CATEGORY_LABELS[item.category]}</span>
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] ${STATUS_STYLES[item.status]}`}>
                        {item.status}
                      </span>
                      <span className="tabular-nums">↓ {item.download_count.toLocaleString()}</span>
                      <span className="tabular-nums">♥ {item.heart_count.toLocaleString()}</span>
                      <span className="text-ink/40">order {item.sort_order}</span>
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button type="button" data-testid={`edit-${item.id}`} onClick={() => onEdit(item)} className={actionBtn} disabled={busyId === item.id}>
                    Edit
                  </button>
                  {item.status !== "published" && (
                    <button type="button" data-testid={`publish-${item.id}`} onClick={() => setStatus(item, "published")} className={actionBtn} disabled={busyId === item.id}>
                      Publish
                    </button>
                  )}
                  {item.status === "published" && (
                    <button type="button" data-testid={`unpublish-${item.id}`} onClick={() => setStatus(item, "draft")} className={actionBtn} disabled={busyId === item.id}>
                      Unpublish
                    </button>
                  )}
                  {item.status !== "archived" ? (
                    <button type="button" data-testid={`archive-${item.id}`} onClick={() => setStatus(item, "archived")} className={actionBtn} disabled={busyId === item.id}>
                      Archive
                    </button>
                  ) : (
                    <button type="button" data-testid={`restore-${item.id}`} onClick={() => setStatus(item, "draft")} className={actionBtn} disabled={busyId === item.id}>
                      Restore
                    </button>
                  )}
                  {confirmDelete === item.id ? (
                    <span className="flex flex-wrap items-center gap-2 rounded-full bg-[#A03D2A]/10 px-3 py-1" data-testid={`confirm-delete-row-${item.id}`}>
                      <span className="text-[12px] font-medium text-[#A03D2A]">Delete this freebie permanently?</span>
                      <button type="button" data-testid={`cancel-delete-${item.id}`} onClick={() => setConfirmDelete(null)} className="min-h-[36px] rounded-full px-3 text-[12px] font-semibold text-ink/70">
                        Cancel
                      </button>
                      <button type="button" data-testid={`confirm-delete-${item.id}`} onClick={() => doDelete(item)} disabled={busyId === item.id} className="min-h-[36px] rounded-full bg-[#A03D2A] px-4 text-[12px] font-semibold text-ivory">
                        Delete
                      </button>
                    </span>
                  ) : (
                    <button type="button" data-testid={`delete-${item.id}`} onClick={() => setConfirmDelete(item.id)} className={`${actionBtn} text-[#A03D2A]/80 hover:text-[#A03D2A]`} disabled={busyId === item.id}>
                      Delete
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
